<template>
  <div>
    <dialog-email
      ref="dialogEmail"
      :contentKey="contentKey"
      :contentMap="contentData"
      :data="emailData"
      @on-submit-email="onSubmitEmail"
    />
    <dialog-confirm
      ref="dialogConfirm"
      :config="configConfirm"
      @confirm="onConfirm"
    >
      <template #footer-confirm="{ clickHandler }">
        <button
          type="button"
          class="btn btn-control btn-border grey btn-size-s"
          @click.prevent="clickHandler('cancel')">
          {{L10n('cancel')}}
        </button>
        <button
          type="button"
          class="btn btn-control btn-border blue btn-size-s"
          ref="btnConfirm"
          @click.prevent="clickHandler('confirm')">
          {{L10n('confirm')}}
        </button>
      </template>
    </dialog-confirm>
    <dialog-alert
      ref="dialogAlert"
      :config="configAlert"
    >
    </dialog-alert>
  </div>
</template>

<script>
import { ServiceRFQs } from '@/services/RFQs'
import { toDate, toPriceCurrency } from '@/utils/filters'
import { ServiceContacts } from '@/services/Contacts'
import { createLink } from '@/utils/templates'
import { api } from '@/services/api'
import { ServiceEmailMessages } from '@/services/email-messages'
import OpenFile from '@/modules/OpenFile'

export default {
  name: 'SendQuoteToCustomerEmail',
  components: {
    'dialog-email': () => import(/* webpackChunkName: 'Dialogs' */ '@/components/Dialogs/DialogEmail/index'),
    'dialog-confirm': () => import(/* webpackChunkName: 'Dialogs' */ '@/components/Dialogs/DialogConfirm'),
    'dialog-alert': () => import(/* webpackChunkName: 'Dialogs' */ '@/components/Dialogs/DialogAlert')
  },
  props: {
    rfqID: { required: true },
    rfq3ID: { required: true }
  },
  data () {
    return {
      noStock: false,
      isFollowUp: false,
      dataRFQ: {},
      dataRFQ3: {},
      emailData: {},
      contentData: {}
    }
  },
  computed: {
    configConfirm () {
      return {
        title: this.L10n('confirm'),
        text: this.L10n('already-sent-are-you-sure')
      }
    },
    configAlert () {
      return {
        title: this.L10n('general-warning'),
        text: this.L10n('please-select-contact')
      }
    },
    contentKey () {
      return this.isFollowUp
        ? 'quote-followup'
        : this.noStock
          ? 'quote-no-stock-to-customer'
          : 'quote-to-customer'
    },
    emailSubject () {
      let partNumber = this.dataRFQ3.partNumber || this.dataRFQ.partNumber || ''
      return this.isFollowUp
        ? `Quote follow-up from Bel-Air Express - ${partNumber}`
        : this.noStock
          ? `Your RFQ - P/N: ${partNumber} - No Stock`
          : `Quote from Bel-Air Express - P/N: ${partNumber}`
    },
    currentEmployee () {
      return this.$store.getters.get('currentEmployee')
    }
  },
  methods: {
    async show ({ noStock, staticRFQData, isFollowUp }) {
      this.noStock = !!noStock
      this.isFollowUp = !!isFollowUp

      let result
      if (staticRFQData) {
        result = await this.loadDataByStatic(staticRFQData)
      } else {
        result = await this.loadData()
      }
      this.contentData = this._createContentData(result)

      if (!this.dataRFQ.customerContactID) {
        return this._showAlert()
      } else {
        return this._showEmail()
      }
    },
    hide () {
      this.noStock = false
      this.isFollowUp = false
      this.dataRFQ = {}
      this.dataRFQ3 = {}
      this.contentData = {}

      this._hideEmail()
      this._hideAlert()
      this._hideConfirm()
    },
    onSubmitEmail (data) {
      if (this.dataRFQ3.sendMail && !this.isFollowUp) {
        this._showConfirm(data)
      } else {
        this.requestSubmitEmail(data)
      }
    },
    onConfirm (data) {
      this.requestSubmitEmail(data)
    },
    requestSubmitEmail (data) {
      return ServiceEmailMessages.send(data)
        .then(() => {
          if (this.rfq3ID) {
            return this.requestUpdateRFQ3()
          }
        })
        .then(() => {
          this.requestSuccessHandler()
          this.$emit('onSuccess')
        })
        .catch((err) => {
          this.requestErrorHandler(err)
        })
        .finally(() => {
          this.hide()
        })
    },
    requestUpdateRFQ3 () {
      let data = {
        id: this.rfq3ID,
        sendMail: true
      }
      return api
        .patch(`RFQ3s/Patch/${this.rfq3ID}`, data)
    },
    async loadDataByStatic ({ customerContactID, ...rest }) {
      if (!customerContactID) {
        this.dataRFQ = {}
        return {}
      }
      const { data } = await ServiceContacts.getDynamic(customerContactID, { select: 'id as customerContactID, name as customerContactName, email' })
      const dataRFQ = { ...data, ...rest }
      this.dataRFQ = dataRFQ
      return dataRFQ
    },
    loadData () {
      return Promise
        .all([
          this.loadDataRFQ(),
          this.loadDataRFQ3()
        ])
        .then(() => {
          return { ...this.dataRFQ, ...this.dataRFQ3 }
        })
    },
    loadDataRFQ () {
      let params = {
        select: [
          'id', 'customerCompany.name as customerCompanyName', 'customerContact.name as customerContactName', 'baxEmployee',
          'priority', 'customerContactID', 'customerContact.email as email'
        ].join(',')
      }
      return ServiceRFQs.getDynamic(this.rfqID, params)
        .then(({ data }) => {
          this.dataRFQ = data
        })
    },
    loadDataRFQ3 () {
      let params = {
        'select': [
          'id', 'part.partNumber as partNumber', 'sendMail', 'condition.title as conditionTitle', 'qty', 'price',
          'release.title as releaseTitle', 'tagInfoCompany.name as tagInfoCompanyName', 'tagDate', 'supplyDate',
          'currency.title as currencyTitle', 'remarks', 'stockID', 'stock.photoAttached as photoAttached', 'stock.ppwAttached as ppwAttached',
          'stock.ppwPath as ppwPath'
        ].join(','),
        'filter': `id == ${this.rfq3ID}`
      }
      return api
        .get(`RFQ3s/GetAllDynamic`, { params })
        .then(({ data }) => {
          let _data = data.value[0]
          if (_data) {
            this.dataRFQ3 = _data
          } else {
            return Promise.reject(new Error())
          }
        })
    },
    _showEmail () {
      this._createEmailData()
      this.$refs.dialogEmail.show()
    },
    _showAlert () {
      this.$notify
        .warning({
          title: this.L10n('general-warning'),
          message: this.L10n('please-select-contact'),
          position: 'bottom-right'
        })
      this.$refs.dialogAlert.show()
    },
    _showConfirm (data) {
      this.$refs.dialogConfirm.show(data)
    },
    _hideEmail () {
      this.emailData = {}
      this.$refs.dialogEmail.hide()
    },
    _hideAlert () {
      this.$refs.dialogAlert.hide()
    },
    _hideConfirm () {
      this.$refs.dialogConfirm.hide()
    },
    _createEmailData () {
      let subject = this.emailSubject
      let emailToList = []
      this.dataRFQ.email && emailToList.push(this.dataRFQ.email)
      let _data = {
        subject,
        emailToList
      }
      this.emailData = _data
    },
    _createContentData (data) {
      const noValue = ''

      const ppwPath = OpenFile.makeFileUrl(this.currentEmployee, data.ppwPath)
      const photoAttached = OpenFile.makeFileUrl(this.currentEmployee, data.photoAttached)
      const ppwAttached = OpenFile.makeFileUrl(this.currentEmployee, data.ppwAttached)

      let result = {
        'customerContactName': data.customerContactName || noValue,
        'customerCompanyName': data.customerCompanyName || noValue,
        'partNumber': data.partNumber || noValue,
        'condition': data.conditionTitle || noValue,
        'release': data.releaseTitle || noValue,
        'tagInfo': data.tagInfoCompanyName || noValue,
        'tagDate': toDate(data.tagDate) || noValue,
        'supplyDate': data.supplyDate || noValue,
        'qty': data.qty || noValue,
        'price': toPriceCurrency(data.price, data.currencyTitle) || noValue,
        'remarks': data.remarks,
        'photoAttached': createLink(photoAttached, this.L10n('photo-link')) || ' ',
        'ppwAttached': createLink(ppwAttached, this.L10n('ppw-link')) || ' ',
        'ppwPath': createLink(ppwPath, this.L10n('ppw-path')) || ' '
      }
      return result
    }
  }
}
</script>
